import { createTheme } from "@mui/material";

import {
  errorContrast,
  errorDark,
  errorLight,
  errorMain,
  infoContrast,
  infoDark,
  infoLight,
  infoMain,
  primaryContrast,
  primaryDark,
  primaryLight,
  primaryMain,
  secondaryContrast,
  secondaryDark,
  secondaryLight,
  secondaryMain,
  successContrast,
  successDark,
  successLight,
  successMain,
  textDisabled,
  textPrimary,
  textSecondary,
  warningContrast,
  warningDark,
  warningLight,
  warningMain,
  purpleContrast,
  purpleDark,
  purpleLight,
  purpleMain,
} from "./tokens/colors";

import {
  body1LineHeight,
  body1Size,
  body1Weight,
  body2LineHeight,
  body2Size,
  body2Weight,
  captionLineHeight,
  captionSize,
  captionWeight,
  h1LineHeight,
  h1Size,
  h1Weight,
  h2LineHeight,
  h2Size,
  h2Weight,
  h3LineHeight,
  h3Size,
  h3Weight,
  h4LineHeight,
  h4Size,
  h4Weight,
  h5LineHeight,
  h5Size,
  h5Weight,
  h6LineHeight,
  h6Size,
  h6Weight,
  overlineLetterSpacing,
  overlineLineHeight,
  overlineSize,
  overlineTextTransform,
  overlineWeight,
  subtitle1LineHeight,
  subtitle1Size,
  subtitle1Weight,
  subtitle2LineHeight,
  subtitle2Size,
  subtitle2Weight,
} from "./tokens/typography";

declare module "@mui/material/styles" {
  interface Palette {
    purple: Palette["primary"];
  }
  interface PaletteOptions {
    purple?: PaletteOptions["primary"];
  }
}

declare module "@mui/material/Chip" {
  interface ChipPropsColorOverrides {
    purple: true;
  }
}

declare module "@mui/material/SvgIcon" {
  interface SvgIconPropsSizeOverrides {
    xlarge: true;
  }
}

export const theme = createTheme({
  typography: {
    allVariants: {
      color: textPrimary,
    },
    h1: {
      fontSize: h1Size,
      fontWeight: h1Weight,
      lineHeight: h1LineHeight,
    },
    h2: {
      fontSize: h2Size,
      fontWeight: h2Weight,
      lineHeight: h2LineHeight,
    },
    h3: {
      fontSize: h3Size,
      fontWeight: h3Weight,
      lineHeight: h3LineHeight,
    },
    h4: {
      fontSize: h4Size,
      fontWeight: h4Weight,
      lineHeight: h4LineHeight,
    },
    h5: {
      fontSize: h5Size,
      fontWeight: h5Weight,
      lineHeight: h5LineHeight,
    },
    h6: {
      fontSize: h6Size,
      fontWeight: h6Weight,
      lineHeight: h6LineHeight,
    },
    subtitle1: {
      fontSize: subtitle1Size,
      fontWeight: subtitle1Weight,
      lineHeight: subtitle1LineHeight,
    },
    subtitle2: {
      fontSize: subtitle2Size,
      fontWeight: subtitle2Weight,
      lineHeight: subtitle2LineHeight,
    },
    body1: {
      fontSize: body1Size,
      fontWeight: body1Weight,
      lineHeight: body1LineHeight,
    },
    body2: {
      fontSize: body2Size,
      fontWeight: body2Weight,
      lineHeight: body2LineHeight,
    },
    caption: {
      fontSize: captionSize,
      fontWeight: captionWeight,
      lineHeight: captionLineHeight,
    },
    overline: {
      fontSize: overlineSize,
      fontWeight: overlineWeight,
      lineHeight: overlineLineHeight,
      letterSpacing: overlineLetterSpacing,
      textTransform: overlineTextTransform,
    },
  },
  palette: {
    primary: {
      main: primaryMain,
      dark: primaryDark,
      light: primaryLight,
      contrastText: primaryContrast,
    },
    secondary: {
      main: secondaryMain,
      dark: secondaryDark,
      light: secondaryLight,
      contrastText: secondaryContrast,
    },
    error: {
      main: errorMain,
      dark: errorDark,
      light: errorLight,
      contrastText: errorContrast,
    },
    warning: {
      main: warningMain,
      dark: warningDark,
      light: warningLight,
      contrastText: warningContrast,
    },
    info: {
      main: infoMain,
      dark: infoDark,
      light: infoLight,
      contrastText: infoContrast,
    },
    success: {
      main: successMain,
      dark: successDark,
      light: successLight,
      contrastText: successContrast,
    },
    purple: {
      main: purpleMain,
      dark: purpleDark,
      light: purpleLight,
      contrastText: purpleContrast,
    },
    text: {
      primary: textPrimary,
      secondary: textSecondary,
      disabled: textDisabled,
    },
  },
  components: {
    MuiSvgIcon: {
      variants: [
        {
          props: { fontSize: "xlarge" },
          style: {
            fontSize: "3rem",
          },
        },
      ],
    },
  },
});
